import { X } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { en } from '../../locales/en';
import { IconButton } from '../buttons/IconButton';
import type { PrimaryButtonProps } from '../buttons/PrimaryButton';
import { PrimaryButton } from '../buttons/PrimaryButton';
import type { SecondaryButtonProps } from '../buttons/SecondaryButton';
import { SecondaryButton } from '../buttons/SecondaryButton';
import { Text } from '../general/Text';

interface DialogProps {
  title: string;
  children: ReactNode;
  onClose?: () => void;
  primaryButtonProps?: PrimaryButtonProps;
  secondaryButtonProps?: SecondaryButtonProps;
  errorText?: string;
}

function Dialog({ title, children, onClose, primaryButtonProps, secondaryButtonProps, errorText }: DialogProps) {
  const canClose = !!onClose;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000
        }}
        onClick={canClose ? onClose : undefined}
      />

      {/* Dialog */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          zIndex: 1001,
          maxHeight: '80vh',
          overflow: 'auto'
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            paddingBottom: '16px'
          }}
        >
          <Text variant='h1' style={{ marginTop: '8px' }}>
            {title}
          </Text>
          {canClose && (
            <div style={{ position: 'absolute', right: '0px' }}>
              <IconButton icon={X} onClick={onClose} title={en.common.close} />
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '8px', textAlign: 'center' }}>
          {children}
          {(primaryButtonProps || secondaryButtonProps) && (
            <div
              style={{
                display: 'flex',
                gap: '12px',
                marginTop: '16px'
              }}
            >
              {secondaryButtonProps && (
                <div style={{ flex: 1 }}>
                  <SecondaryButton {...secondaryButtonProps} />
                </div>
              )}
              {primaryButtonProps && (
                <div style={{ flex: 1 }}>
                  <PrimaryButton {...primaryButtonProps} />
                </div>
              )}
            </div>
          )}
          {errorText && (
            <Text
              variant='body3'
              style={{
                marginTop: '16px',
                color: 'var(--color-error)',
                wordBreak: 'break-word',
                textAlign: 'center'
              }}
            >
              {errorText}
            </Text>
          )}
        </div>
      </div>
    </>
  );
}

export default Dialog;
